import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Search,
  Building2,
  Phone,
  Mail,
  MapPin,
  Users,
  X,
  Filter,
  ChevronDown,
  ChevronLeft,
  Edit,
  Trash2,
  Save,
  XCircle,
  Maximize2,
  Minimize2,
  Plus,
  Map,
} from "lucide-react";
import apiService from "../services/api";
import "../styles/Customers.css";

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: "all",
    sortBy: "name",
    sortOrder: "asc",
  });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteGroupConfirm, setDeleteGroupConfirm] = useState(null);
  const [modalDeleteConfirm, setModalDeleteConfirm] = useState(false);
  const [editingModal, setEditingModal] = useState(false);
  const [modalEditForm, setModalEditForm] = useState({});
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState({
    customer_name: "",
    site_name: "",
    contact_person: "",
    phone: "",
    email: "",
    address: "",
  });
  const [expandedCustomers, setExpandedCustomers] = useState(new Set());
  const [groupedCustomers, setGroupedCustomers] = useState([]);
  const [employeesExpanded, setEmployeesExpanded] = useState(false);
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [customers, searchTerm, filters]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      // First get all customers (sites)
      const customersResponse = await apiService.getCustomers({ limit: 1000 });
      const customersList = customersResponse.customers || [];

      // Then get all employees to calculate counts
      const employeesResponse = await apiService.getEmployees({ limit: 10000 });
      const employeesList = employeesResponse.employees || [];

      // Calculate employee count per site
      const siteCounts = {};
      employeesList.forEach((emp) => {
        if (emp.customer_id) {
          if (emp.active) {
            siteCounts[emp.customer_id] =
              (siteCounts[emp.customer_id] || 0) + 1;
          }
        }
      });

      // Add employee count to each site
      const sitesWithCounts = customersList.map((site) => ({
        ...site,
        employee_count: siteCounts[site.id] || 0,
      }));

      console.log("Sites with counts:", sitesWithCounts); // Debug
      console.log("First site example:", sitesWithCounts[0]); // Debug - see structure

      // Group sites by customer name
      const grouped = {};
      sitesWithCounts.forEach((site) => {
        // Use 'name' field which contains the customer name
        // If name is empty, use site_name as fallback
        const customerName = site.name || site.site_name || "ללא שם לקוח";
        if (!grouped[customerName]) {
          grouped[customerName] = {
            name: customerName,
            sites: [],
            total_employees: 0,
          };
        }
        grouped[customerName].sites.push(site);
        grouped[customerName].total_employees += site.employee_count;
      });

      console.log("Grouped customers:", grouped); // Debug
      console.log("Number of groups:", Object.keys(grouped).length); // Debug

      // Convert to array and sort
      const groupedArray = Object.values(grouped).sort((a, b) =>
        a.name.localeCompare(b.name, "he"),
      );

      setGroupedCustomers(groupedArray);
      setCustomers(sitesWithCounts);
    } catch (error) {
      console.error("Failed to load customers:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    // Filter individual sites first
    let filtered = [...customers];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (customer) =>
          customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.site_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          customer.code?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Status filter
    if (filters.status !== "all") {
      filtered = filtered.filter((customer) =>
        filters.status === "active" ? customer.active : !customer.active,
      );
    }

    // Re-group filtered sites
    const grouped = {};
    filtered.forEach((site) => {
      const customerName = site.name || "ללא שם לקוח";
      if (!grouped[customerName]) {
        grouped[customerName] = {
          customer_name: customerName,
          name: customerName,
          sites: [],
          total_employees: 0,
        };
      }
      grouped[customerName].sites.push(site);
      grouped[customerName].total_employees += site.employee_count || 0;
    });

    // Convert to array and sort
    let groupedArray = Object.values(grouped);

    // Sort grouped customers
    groupedArray.sort((a, b) => {
      let aVal, bVal;

      switch (filters.sortBy) {
        case "name":
          aVal = a.name || "";
          bVal = b.name || "";
          break;
        case "employees":
          aVal = a.total_employees || 0;
          bVal = b.total_employees || 0;
          break;
        default:
          aVal = a.name || "";
          bVal = b.name || "";
      }

      if (filters.sortOrder === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredCustomers(filtered);
    setGroupedCustomers(groupedArray);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);

    if (value.length > 0) {
      const matches = customers
        .filter(
          (customer) =>
            customer.name?.toLowerCase().includes(value.toLowerCase()) ||
            customer.site_name?.toLowerCase().includes(value.toLowerCase()),
        )
        .slice(0, 5);
      setSuggestions(matches);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (customer) => {
    setSearchTerm(customer.name);
    setShowSuggestions(false);
    handleRowClick(customer, null);
  };

  const handleRowClick = async (customer, event) => {
    // Don't open modal if clicking on action buttons
    if (event && event.target && event.target.closest(".action-buttons")) {
      return;
    }

    try {
      // Get customer details
      const response = await apiService.getCustomer(customer.id);
      const customerData = response.customer;

      // Get employees for this customer
      const employeesResponse = await apiService.getEmployees({ limit: 10000 });
      const customerEmployees = (employeesResponse.employees || []).filter(
        (emp) => emp.customer_id === customer.id,
      );

      setSelectedCustomer({
        ...customerData,
        employees: customerEmployees,
      });
      setShowModal(true);
    } catch (error) {
      console.error("Failed to load customer details:", error);
    }
  };

  const handleEdit = (customer, event) => {
    event.stopPropagation();
    setEditingCustomer(customer.id);
    setEditForm({
      customer_name: customer.name || "",
      customer_code: customer.code || "",
      site_name: customer.site_name || "",
      site_code: customer.site_code || "",
      office: customer.office || "",
    });
  };

  const handleCancelEdit = (event) => {
    event.stopPropagation();
    setEditingCustomer(null);
    setEditForm({});
  };

  const handleSaveEdit = async (customerId, event) => {
    event.stopPropagation();
    try {
      await apiService.updateCustomer(customerId, editForm);
      setEditingCustomer(null);
      setEditForm({});
      loadCustomers();
    } catch (error) {
      console.error("Failed to update customer:", error);
      alert("שגיאה בעדכון לקוח");
    }
  };

  const handleDelete = (customer, event) => {
    event.stopPropagation();
    setDeleteConfirm(customer.id);
  };

  const confirmDelete = async (customerId, event) => {
    event.stopPropagation();
    try {
      await apiService.deleteCustomer(customerId);
      setDeleteConfirm(null);
      loadCustomers();
    } catch (error) {
      console.error("Failed to delete customer:", error);
      alert("שגיאה במחיקת לקוח");
    }
  };

  const cancelDelete = (event) => {
    event.stopPropagation();
    setDeleteConfirm(null);
  };

  const handleDeleteCustomerGroup = (customerName, event) => {
    event.stopPropagation();
    setDeleteGroupConfirm(customerName);
  };

  const confirmDeleteGroup = async (customerName, event) => {
    event.stopPropagation();
    try {
      const result = await apiService.deleteCustomerByName(customerName);
      setDeleteGroupConfirm(null);
      loadCustomers();
      alert(`נמחקו ${result.deleted_count} אתרים ועובדיהם נותקו בהצלחה`);
    } catch (error) {
      console.error("Failed to delete customer group:", error);
      alert("שגיאה במחיקת הלקוח");
    }
  };

  const cancelDeleteGroup = (event) => {
    event.stopPropagation();
    setDeleteGroupConfirm(null);
  };

  const handleCleanupNoHours = async () => {
    if (
      !window.confirm(
        'האם אתה בטוח שברצונך למחוק את כל הלקוחות המכילים "לא דווחו שעות"? פעולה זו בלתי הפיכה!',
      )
    )
      return;
    try {
      const result = await apiService.cleanupNoHoursCustomers();
      alert(`נמחקו ${result.deleted_count} לקוחות בהצלחה`);
      loadCustomers();
    } catch (error) {
      console.error("Failed to cleanup no-hours customers:", error);
      alert("שגיאה בהסרת לקוחות");
    }
  };

  const handleModalDelete = () => {
    setModalDeleteConfirm(true);
  };

  const confirmModalDelete = async () => {
    try {
      await apiService.deleteCustomer(selectedCustomer.id);
      setModalDeleteConfirm(false);
      closeModal();
      loadCustomers();
    } catch (error) {
      console.error("Failed to delete customer:", error);
      alert("שגיאה במחיקת לקוח");
    }
  };

  const cancelModalDelete = () => {
    setModalDeleteConfirm(false);
  };

  const handleModalEdit = () => {
    setEditingModal(true);
    setModalEditForm({
      customer_name: selectedCustomer.name || "",
      customer_code: selectedCustomer.code || "",
      site_name: selectedCustomer.site_name || "",
      site_code: selectedCustomer.site_code || "",
      office: selectedCustomer.office || "",
    });
  };

  const handleCancelModalEdit = () => {
    setEditingModal(false);
    setModalEditForm({});
  };

  const handleSaveModalEdit = async () => {
    try {
      await apiService.updateCustomer(selectedCustomer.id, modalEditForm);
      setEditingModal(false);
      setModalEditForm({});

      // Reload customer details
      const response = await apiService.getCustomer(selectedCustomer.id);
      const customerData = response.customer;

      // Get employees for this customer
      const employeesResponse = await apiService.getEmployees({ limit: 10000 });
      const customerEmployees = (employeesResponse.employees || []).filter(
        (emp) => emp.customer_id === selectedCustomer.id,
      );

      setSelectedCustomer({
        ...customerData,
        employees: customerEmployees,
      });

      // Reload customers list
      loadCustomers();
    } catch (error) {
      console.error("Failed to update customer:", error);
      alert("שגיאה בעדכון לקוח");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedCustomer(null);
    setEditingModal(false);
    setModalEditForm({});
    setModalDeleteConfirm(false);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleNewCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomerForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    try {
      await apiService.createCustomer(newCustomerForm);
      alert("לקוח נוסף בהצלחה!");
      setShowAddCustomerModal(false);
      setNewCustomerForm({
        customer_name: "",
        site_name: "",
        contact_person: "",
        phone: "",
        email: "",
        address: "",
      });
      loadCustomers();
    } catch (error) {
      console.error("Failed to add customer:", error);
      alert("שגיאה בהוספת לקוח");
    }
  };

  const toggleCustomerExpand = (customerName) => {
    setExpandedCustomers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(customerName)) {
        newSet.delete(customerName);
      } else {
        newSet.add(customerName);
      }
      return newSet;
    });
  };

  const handleEmployeeClick = (employee) => {
    // Navigate to Employees page with employee ID as search parameter
    navigate(`/employees?search=${employee.employee_id}`);
  };

  if (loading) {
    return (
      <div className='customers-page'>
        <div className='loading-container'>
          <div className='spinner'></div>
          <p>טוען לקוחות...</p>
        </div>
      </div>
    );
  }

  // Calculate statistics
  const totalCustomers = groupedCustomers.length;
  const totalSites = customers.length;
  const totalEmployees = groupedCustomers.reduce(
    (sum, c) => sum + c.total_employees,
    0,
  );

  return (
    <div className='customers-page'>
      <div className='page-header'>
        <div>
          <h1>לקוחות</h1>
          <p>ניהול לקוחות ואתרים</p>
        </div>
        <div className='header-actions'>
          {(hasPermission("editor") || hasPermission("admin")) && (
            <button
              className='btn-primary'
              onClick={() => setShowAddCustomerModal(true)}
            >
              <Plus size={18} />
              הוסף לקוח
            </button>
          )}
          {hasPermission("admin") && (
            <button
              className='btn-danger-outline'
              onClick={handleCleanupNoHours}
              title='מחק כל לקוחות "לא דווחו שעות"'
            >
              <Trash2 size={16} />
              נקה לא דווחו שעות
            </button>
          )}
        </div>
      </div>

      {/* Customer Statistics */}
      <h3 className='stats-section-title'>סטטיסטיקות לקוחות</h3>
      <div className='customer-stats'>
        <div className='stat-card'>
          <div className='stat-icon total'>
            <Building2 size={24} />
          </div>
          <span className='stat-icon-title'>לקוחות</span>
          <div className='stat-info'>
            <span className='stat-value'>{totalCustomers}</span>
            <span className='stat-label'>סה"כ לקוחות</span>
          </div>
        </div>
        <div className='stat-card'>
          <div className='stat-icon sites'>
            <Map size={24} />
          </div>
          <span className='stat-icon-title'>אתרים</span>
          <div className='stat-info'>
            <span className='stat-value'>{totalSites}</span>
            <span className='stat-label'>סה"כ אתרים</span>
          </div>
        </div>
        <div className='stat-card'>
          <div className='stat-icon employees'>
            <Users size={24} />
          </div>
          <span className='stat-icon-title'>עובדים</span>
          <div className='stat-info'>
            <span className='stat-value'>{totalEmployees}</span>
            <span className='stat-label'>סה"כ עובדים</span>
          </div>
        </div>
      </div>

      <div className='filters-section'>
        <div className='filters-header'>
          <div className='search-container'>
            <Search className='search-icon' size={20} />
            <input
              type='text'
              className='search-input'
              placeholder='חפש לקוח או אתר...'
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => searchTerm && setShowSuggestions(true)}
            />
            {searchTerm && (
              <button
                className='clear-search'
                onClick={() => {
                  setSearchTerm("");
                  setShowSuggestions(false);
                }}
              >
                <X size={18} />
              </button>
            )}

            {showSuggestions && suggestions.length > 0 && (
              <div className='suggestions-dropdown'>
                {suggestions.map((customer) => (
                  <div
                    key={customer.id}
                    className='suggestion-item'
                    onClick={() => handleSuggestionClick(customer)}
                  >
                    <Building2 size={16} />
                    <div className='suggestion-content'>
                      <span className='suggestion-name'>{customer.name}</span>
                      {customer.contact_person && (
                        <span className='suggestion-detail'>
                          {customer.contact_person}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            className={`btn-filter ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
            מסננים
          </button>
        </div>

        {showFilters && (
          <div className='filters-panel'>
            <div className='filter-group'>
              <label>סטטוס:</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value='all'>הכל</option>
                <option value='active'>פעיל</option>
                <option value='inactive'>לא פעיל</option>
              </select>
            </div>

            <div className='filter-group'>
              <label>מיין לפי:</label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange("sortBy", e.target.value)}
              >
                <option value='name'>שם</option>
                <option value='id'>מזהה</option>
                <option value='employees'>מספר עובדים</option>
              </select>
            </div>

            <div className='filter-group'>
              <label>סדר:</label>
              <select
                value={filters.sortOrder}
                onChange={(e) =>
                  handleFilterChange("sortOrder", e.target.value)
                }
              >
                <option value='asc'>עולה</option>
                <option value='desc'>יורד</option>
              </select>
            </div>
          </div>
        )}
      </div>

      <div className='customers-table-container'>
        <table className='customers-table'>
          <thead>
            <tr>
              <th style={{ width: "50px" }}></th>
              <th>שם לקוח</th>
              <th>מספר אתרים</th>
              <th>סה"כ עובדים</th>
              <th style={{ width: "80px" }}></th>
            </tr>
          </thead>
          <tbody>
            {groupedCustomers.length === 0 ? (
              <tr>
                <td colSpan='4' className='no-data'>
                  לא נמצאו לקוחות
                </td>
              </tr>
            ) : (
              groupedCustomers.map((customer) => {
                const isExpanded = expandedCustomers.has(customer.name);
                return (
                  <React.Fragment key={customer.name}>
                    {/* Customer Row */}
                    <tr
                      className='customer-group-row'
                      onClick={() => toggleCustomerExpand(customer.name)}
                    >
                      <td className='expand-cell'>
                        {isExpanded ? (
                          <ChevronDown size={20} />
                        ) : (
                          <ChevronLeft size={20} />
                        )}
                      </td>
                      <td className='customer-name'>
                        <Building2 size={18} />
                        <span>{customer.name}</span>
                      </td>
                      <td className='sites-count'>
                        <span className='count-badge'>
                          {customer.sites.length} אתרים
                        </span>
                      </td>
                      <td className='employees-count'>
                        <div className='count-badge'>
                          <Users size={16} />
                          <span>{customer.total_employees}</span>
                        </div>
                      </td>
                      <td
                        className='action-buttons'
                        onClick={(e) => e.stopPropagation()}
                      >
                        {hasPermission("admin") &&
                          (deleteGroupConfirm === customer.name ? (
                            <div className='delete-confirm'>
                              <span>מחק כל האתרים?</span>
                              <button
                                className='confirm-delete-btn'
                                onClick={(e) =>
                                  confirmDeleteGroup(customer.name, e)
                                }
                              >
                                כן
                              </button>
                              <button
                                className='cancel-delete-btn'
                                onClick={(e) => cancelDeleteGroup(e)}
                              >
                                לא
                              </button>
                            </div>
                          ) : (
                            <div className='action-icons'>
                              <button
                                className='action-btn delete-btn'
                                onClick={(e) =>
                                  handleDeleteCustomerGroup(customer.name, e)
                                }
                                title='מחק לקוח וכל אתריו'
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}
                      </td>
                    </tr>

                    {/* Expanded Sites */}
                    {isExpanded &&
                      customer.sites.map((site) => (
                        <tr
                          key={site.id}
                          className='site-row'
                          onClick={(e) => handleRowClick(site, e)}
                        >
                          <td></td>
                          <td className='site-name'>
                            <MapPin size={14} />
                            <span>
                              {site.name && site.site_name
                                ? `${site.name} - ${site.site_name}`
                                : site.site_name || site.name || "ללא שם אתר"}
                            </span>
                          </td>
                          <td className='site-id'>
                            <span className='site-id-badge'>#{site.id}</span>
                          </td>
                          <td className='employees-count'>
                            <div className='count-badge small'>
                              <Users size={14} />
                              <span>{site.employee_count || 0}</span>
                            </div>
                          </td>
                          <td
                            className='action-buttons'
                            onClick={(e) => e.stopPropagation()}
                          >
                            {hasPermission("admin") &&
                              (deleteConfirm === site.id ? (
                                <div className='delete-confirm'>
                                  <span>מחק?</span>
                                  <button
                                    className='confirm-delete-btn'
                                    onClick={(e) => confirmDelete(site.id, e)}
                                  >
                                    כן
                                  </button>
                                  <button
                                    className='cancel-delete-btn'
                                    onClick={(e) => cancelDelete(e)}
                                  >
                                    לא
                                  </button>
                                </div>
                              ) : (
                                <div className='action-icons'>
                                  <button
                                    className='action-btn delete-btn'
                                    onClick={(e) => handleDelete(site, e)}
                                    title='מחק אתר'
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              ))}
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showModal && selectedCustomer && (
        <div className='modal-overlay' onClick={closeModal}>
          <div className='modal-content' onClick={(e) => e.stopPropagation()}>
            <div className='modal-header'>
              <h2>
                <Building2 size={24} />
                פרטי לקוח
              </h2>
              <div className='modal-header-actions'>
                {(hasPermission("editor") || hasPermission("admin")) &&
                  !editingModal && (
                    <button
                      className='edit-modal-btn'
                      onClick={handleModalEdit}
                    >
                      <Edit size={18} />
                      <span>ערוך</span>
                    </button>
                  )}
                {hasPermission("admin") &&
                  !editingModal &&
                  (modalDeleteConfirm ? (
                    <div className='delete-confirm'>
                      <span>מחק לקוח?</span>
                      <button
                        className='confirm-delete-btn'
                        onClick={confirmModalDelete}
                      >
                        כן
                      </button>
                      <button
                        className='cancel-delete-btn'
                        onClick={cancelModalDelete}
                      >
                        לא
                      </button>
                    </div>
                  ) : (
                    <button
                      className='delete-modal-btn'
                      onClick={handleModalDelete}
                    >
                      <Trash2 size={18} />
                      <span>מחק</span>
                    </button>
                  ))}
                <button className='close-button' onClick={closeModal}>
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className='modal-body'>
              {editingModal ? (
                <div className='detail-section'>
                  <h3>ערוך לקוח</h3>
                  <div className='modal-edit-form'>
                    <div className='form-group'>
                      <label>מזהה לקוח:</label>
                      <input type='text' value={selectedCustomer.id} disabled />
                    </div>
                    <div className='form-group'>
                      <label>קוד לקוח:</label>
                      <input
                        type='text'
                        value={modalEditForm.customer_code}
                        onChange={(e) =>
                          setModalEditForm({
                            ...modalEditForm,
                            customer_code: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className='form-group'>
                      <label>שם לקוח:</label>
                      <input
                        type='text'
                        value={modalEditForm.customer_name}
                        onChange={(e) =>
                          setModalEditForm({
                            ...modalEditForm,
                            customer_name: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className='form-group'>
                      <label>שם אתר:</label>
                      <input
                        type='text'
                        value={modalEditForm.site_name}
                        onChange={(e) =>
                          setModalEditForm({
                            ...modalEditForm,
                            site_name: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className='form-group'>
                      <label>קוד אתר:</label>
                      <input
                        type='text'
                        value={modalEditForm.site_code}
                        onChange={(e) =>
                          setModalEditForm({
                            ...modalEditForm,
                            site_code: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className='form-group'>
                      <label>משרד:</label>
                      <input
                        type='text'
                        value={modalEditForm.office}
                        onChange={(e) =>
                          setModalEditForm({
                            ...modalEditForm,
                            office: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className='modal-edit-actions'>
                      <button
                        className='save-modal-btn'
                        onClick={handleSaveModalEdit}
                      >
                        <Save size={18} />
                        <span>שמור</span>
                      </button>
                      <button
                        className='cancel-modal-btn'
                        onClick={handleCancelModalEdit}
                      >
                        <XCircle size={18} />
                        <span>ביטול</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className='detail-section'>
                  <h3>מידע כללי</h3>
                  <div className='detail-grid'>
                    <div className='detail-item'>
                      <label>מזהה לקוח:</label>
                      <span>{selectedCustomer.id}</span>
                    </div>
                    <div className='detail-item'>
                      <label>קוד לקוח:</label>
                      <span>{selectedCustomer.code || "N/A"}</span>
                    </div>
                    <div className='detail-item'>
                      <label>שם לקוח:</label>
                      <span>{selectedCustomer.name || "N/A"}</span>
                    </div>
                    <div className='detail-item'>
                      <label>שם אתר:</label>
                      <span>{selectedCustomer.site_name || "N/A"}</span>
                    </div>
                    <div className='detail-item'>
                      <label>קוד אתר:</label>
                      <span>{selectedCustomer.site_code || "N/A"}</span>
                    </div>
                    <div className='detail-item'>
                      <label>משרד:</label>
                      <span>{selectedCustomer.office || "N/A"}</span>
                    </div>
                  </div>
                </div>
              )}

              {selectedCustomer.employees &&
                selectedCustomer.employees.length > 0 && (
                  <div
                    className={`detail-section employees-section ${
                      employeesExpanded ? "expanded" : ""
                    }`}
                  >
                    <div className='section-header-with-actions'>
                      <h3>
                        <Users size={20} />
                        עובדים ({selectedCustomer.employees.length})
                      </h3>
                      <button
                        className='expand-btn'
                        onClick={() => setEmployeesExpanded(!employeesExpanded)}
                        title={employeesExpanded ? "צמצם" : "הרחב"}
                      >
                        {employeesExpanded ? (
                          <Minimize2 size={18} />
                        ) : (
                          <Maximize2 size={18} />
                        )}
                      </button>
                    </div>
                    <div className='employees-list'>
                      {selectedCustomer.employees.map((employee) => (
                        <div
                          key={employee.id}
                          className='employee-item clickable'
                          onClick={() => handleEmployeeClick(employee)}
                          title='לחץ לצפייה בעמוד עובדים'
                        >
                          <div className='employee-header'>
                            <div className='employee-title'>
                              <span className='employee-name'>
                                {employee.employee_name}
                              </span>
                              <span
                                className={`employee-status ${
                                  employee.active ? "active" : "inactive"
                                }`}
                              >
                                {employee.active ? "פעיל" : "לא פעיל"}
                              </span>
                            </div>
                          </div>

                          <div className='employee-data-grid'>
                            {employee.id && (
                              <div className='employee-data-item'>
                                <label>מספר עובד:</label>
                                <span>{employee.employee_number}</span>
                              </div>
                            )}

                            <div className='employee-data-item'>
                              <label>מספר עובד:</label>
                              <span className='badge-id'>
                                {employee.employee_id}
                              </span>
                            </div>

                            {employee.type && (
                              <div className='employee-data-item'>
                                <label>סוג:</label>
                                <span className='badge-type'>
                                  {employee.type}
                                </span>
                              </div>
                            )}

                            {employee.phone && (
                              <div className='employee-data-item'>
                                <label>טלפון:</label>
                                <span>
                                  <Phone size={12} />
                                  {employee.phone}
                                </span>
                              </div>
                            )}

                            {employee.hour_rate && (
                              <div className='employee-data-item'>
                                <label>תעריף שעתי:</label>
                                <span>₪{employee.hour_rate}</span>
                              </div>
                            )}

                            {employee.weekend_hour_rate && (
                              <div className='employee-data-item'>
                                <label>תעריף שעתי סופ"ש:</label>
                                <span>₪{employee.weekend_hour_rate}</span>
                              </div>
                            )}

                            {employee.payment_method && (
                              <div className='employee-data-item'>
                                <label>אמצעי תשלום:</label>
                                <span
                                  className={`badge-payment ${employee.payment_method}`}
                                >
                                  {employee.payment_method}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {selectedCustomer.notes && (
                <div className='detail-section'>
                  <h3>הערות</h3>
                  <p className='notes-text'>{selectedCustomer.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <div
          className='modal-overlay'
          onClick={() => setShowAddCustomerModal(false)}
        >
          <div
            className='modal-content add-customer-modal'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='modal-header'>
              <h2>הוסף לקוח חדש</h2>
              <button
                className='close-modal'
                onClick={() => setShowAddCustomerModal(false)}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleAddCustomer} className='add-customer-form'>
              <div className='form-grid'>
                <div className='form-field'>
                  <label>
                    שם לקוח <span className='required'>*</span>
                  </label>
                  <input
                    type='text'
                    name='customer_name'
                    value={newCustomerForm.customer_name}
                    onChange={handleNewCustomerChange}
                    placeholder='הזן שם לקוח'
                    required
                  />
                </div>

                <div className='form-field'>
                  <label>שם אתר</label>
                  <input
                    type='text'
                    name='site_name'
                    value={newCustomerForm.site_name}
                    onChange={handleNewCustomerChange}
                    placeholder='הזן שם אתר'
                  />
                </div>

                <div className='form-field'>
                  <label>איש קשר</label>
                  <input
                    type='text'
                    name='contact_person'
                    value={newCustomerForm.contact_person}
                    onChange={handleNewCustomerChange}
                    placeholder='הזן שם איש קשר'
                  />
                </div>

                <div className='form-field'>
                  <label>טלפון</label>
                  <input
                    type='tel'
                    name='phone'
                    value={newCustomerForm.phone}
                    onChange={handleNewCustomerChange}
                    placeholder='הזן מספר טלפון'
                  />
                </div>

                <div className='form-field'>
                  <label>אימייל</label>
                  <input
                    type='email'
                    name='email'
                    value={newCustomerForm.email}
                    onChange={handleNewCustomerChange}
                    placeholder='הזן כתובת אימייל'
                  />
                </div>

                <div className='form-field full-width'>
                  <label>כתובת</label>
                  <input
                    type='text'
                    name='address'
                    value={newCustomerForm.address}
                    onChange={handleNewCustomerChange}
                    placeholder='הזן כתובת'
                  />
                </div>
              </div>

              <div className='form-actions'>
                <button
                  type='button'
                  className='btn-cancel'
                  onClick={() => setShowAddCustomerModal(false)}
                >
                  ביטול
                </button>
                <button type='submit' className='btn-submit'>
                  <Building2 size={18} />
                  צור לקוח
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
